import {formatTimeZoneDate, COMMENTS_DATE_FORMAT} from '@hconnect/uikit'
import {MarkdownText} from '@hconnect/uikit/src/lib2'
import {Box, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import React, {ReactNode} from 'react'

import {CommentMoreButton} from './CommentMoreButton'
import {CommonComment, EditCommentProps, DeleteCommentProps} from './comments.types'

type CommentItemProps<T> = {
  comment: T
  timeZone: string
  deleteCommentProps?: DeleteCommentProps
  editCommentProps?: EditCommentProps
  renderPrefix?: (commentId: string) => ReactNode | undefined
  translate: TFunction
  language: string
  noTextGutter?: boolean
  dateFormat?: string
  customCommentPostfix?: (comment: T) => ReactNode
}

export const CommentItem = <T extends CommonComment>({
  comment,
  deleteCommentProps,
  editCommentProps,
  renderPrefix,
  timeZone,
  translate,
  language,
  noTextGutter,
  dateFormat = COMMENTS_DATE_FORMAT,
  customCommentPostfix
}: CommentItemProps<T>) => {
  return (
    <Box display="flex" flexDirection="row" py={1} data-test-id={`comment-${comment.id}`}>
      <>
        <Box flex={1} pr={2}>
          {renderPrefix?.(comment.id)}
          <Typography component="div" gutterBottom={!noTextGutter} data-test-id="comment-item-text">
            <MarkdownText>{comment.text}</MarkdownText>
          </Typography>
          {customCommentPostfix?.(comment) || (
            <Typography variant="caption" data-test-id="comment-author-date-label">
              {translate('comments.byOn', {
                by: comment.updatedBy ?? comment.createdBy,
                on: formatTimeZoneDate(
                  comment.updatedOn?.toISOString() ?? comment.createdOn.toISOString(),
                  timeZone,
                  dateFormat,
                  language
                )
              })}
            </Typography>
          )}
        </Box>
        {!!(deleteCommentProps && editCommentProps) && (
          <Box data-test-id="comment-item-actions">
            <CommentMoreButton
              comment={comment}
              deleteCommentProps={deleteCommentProps}
              moreButtonTestId={`commentItem-more-button-${comment.id}`}
              editCommentProps={editCommentProps}
              translate={translate}
            />
          </Box>
        )}
      </>
    </Box>
  )
}
